import { Badge, Tooltip } from '@mantine/core'
import { IconDownload } from '@tabler/icons-react'
import type { Attachment } from '@medplum/fhirtypes'
import type { AuthorizationStatus } from './useAuthorizationStatus'
import { useAuthorizationStatus } from './useAuthorizationStatus'
import { logError } from '@/utils/errorUtils'

const CONFIG: Record<AuthorizationStatus, { label: string; color: string }> = {
  missing: { label: 'Missing Authorization', color: 'gray' },
  completed: { label: 'Authorization Completed', color: 'green' },
  expired: { label: 'Authorization Expired', color: 'red' },
  'needs-signature': { label: 'Needs New Signature', color: 'orange' },
}

/** Download the signed authorization PDF (with the patient's signature). */
async function downloadSignedAuthorization(attachment: Attachment) {
  const fileName = `${attachment.title || 'authorization-to-release-records'}.pdf`
  if (attachment.url) {
    const res = await fetch(attachment.url)
    const blob = await res.blob()
    const tempUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = tempUrl
    a.download = fileName
    a.click()
    URL.revokeObjectURL(tempUrl)
  } else if (attachment.data) {
    // base64-encoded PDF fallback
    const a = document.createElement('a')
    a.href = `data:${attachment.contentType || 'application/pdf'};base64,${attachment.data}`
    a.download = fileName
    a.click()
  }
}

// Presentational badge — renders from already-resolved status (no fetch).
export const AuthorizationStatusView = ({
  status,
  loading,
}: {
  status: AuthorizationStatus
  loading: boolean
}) => {
  if (loading)
    return (
      <Badge color="gray" variant="light">
        Checking…
      </Badge>
    )
  const cfg = CONFIG[status]
  return (
    <Badge color={cfg.color} variant="light">
      {cfg.label}
    </Badge>
  )
}

// Self-fetching badge — for standalone use (e.g. Demographics). Once signed, a
// download icon is shown INSIDE the badge to grab the signed authorization PDF.
export const AuthorizationStatusBadge = ({ patientId }: { patientId: string }) => {
  const { status, loading, attachment } = useAuthorizationStatus(patientId)
  if (loading)
    return (
      <Badge color="gray" variant="light">
        Checking…
      </Badge>
    )
  const cfg = CONFIG[status]
  const downloadable = status === 'completed' && attachment
  return (
    <Badge
      color={cfg.color}
      variant="light"
      rightSection={
        downloadable ? (
          <Tooltip label="Download signed authorization" withArrow>
            <IconDownload
              size={13}
              stroke={2.5}
              className="cursor-pointer"
              aria-label="Download signed authorization"
              onClick={() => {
                downloadSignedAuthorization(attachment).catch((err) =>
                  logError('AuthorizationRelease', 'download signed authorization', err),
                )
              }}
            />
          </Tooltip>
        ) : undefined
      }
    >
      {cfg.label}
    </Badge>
  )
}
